import type { ReactElement } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Rectangle,
  XAxis,
  YAxis,
  useActiveTooltipLabel,
  type BarShapeProps,
  type RectangleProps,
} from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  type ChartConfig,
} from "components/ui/chart";
import { cn } from "utils";
import {
  cursorTooltipWrapperProps,
  renderCursorTooltip,
} from "./cursorTooltip";

export interface SalesBarSeries {
  /** Must match a key in `config`. */
  key: string;
  stackId?: string;
  radius?: RectangleProps["radius"];
}

export type SalesBarTooltipPosition = "default" | "cursor";

export interface SalesBarChartProps {
  data: Record<string, unknown>[];
  config: ChartConfig;
  series: SalesBarSeries[];
  xKey: string;
  xTickFormatter?: (value: string) => string;
  yTickFormatter?: (value: number) => string;
  tooltipContent?: ReactElement;
  /** `cursor`: centred on hover, clamped to viewport and clip ancestors. */
  tooltipPosition?: SalesBarTooltipPosition;
  /** Width guess for placement before the first layout measure. */
  tooltipEstimatedWidth?: number;
  ariaLabel?: string;
  className?: string;
  overlay?: ReactElement;
}

const DIMMED_OPACITY = 0.4;

// Beyond this count, horizontal date labels overlap without tilting.
const ANGLED_LABEL_BAR_THRESHOLD = 12;

/** Dims non-active buckets; follows keyboard focus via useActiveTooltipLabel. */
const DimmableBar = ({
  xKey,
  ...shapeProps
}: BarShapeProps & { xKey: string }) => {
  const activeLabel = useActiveTooltipLabel();
  const dimmed =
    activeLabel !== undefined && shapeProps.payload?.[xKey] !== activeLabel;
  return (
    <Rectangle {...shapeProps} fillOpacity={dimmed ? DIMMED_OPACITY : 1} />
  );
};

export const SalesBarChart = ({
  data,
  config,
  series,
  xKey,
  xTickFormatter,
  yTickFormatter,
  tooltipContent,
  tooltipPosition = "default",
  tooltipEstimatedWidth,
  ariaLabel,
  className,
  overlay,
}: SalesBarChartProps) => {
  const angledLabels = data.length > ANGLED_LABEL_BAR_THRESHOLD;
  return (
    <ChartContainer
      config={config}
      role="group"
      aria-label={ariaLabel}
      className={cn(
        "aspect-auto h-[260px] w-full",
        "[&_.recharts-wrapper]:outline-none [&_.recharts-wrapper:focus]:outline-none [&_.recharts-wrapper:focus-visible]:outline-none [&_.recharts-surface]:outline-none",
        className,
      )}
    >
      <BarChart data={data} margin={{ top: 60, right: 4, bottom: 0, left: 4 }}>
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey={xKey}
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          minTickGap={16}
          // Taller axis fits rotated tick text.
          angle={angledLabels ? -45 : 0}
          textAnchor={angledLabels ? "end" : "middle"}
          height={angledLabels ? 56 : 30}
          tickFormatter={
            xTickFormatter
              ? (value) => xTickFormatter(String(value))
              : undefined
          }
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          width={48}
          tickFormatter={
            yTickFormatter
              ? (value) => yTickFormatter(Number(value))
              : undefined
          }
        />
        <ChartTooltip
          cursor={false}
          {...(tooltipPosition === "cursor" && tooltipContent
            ? cursorTooltipWrapperProps
            : {})}
          content={
            tooltipPosition === "cursor" && tooltipContent
              ? (props) =>
                  renderCursorTooltip(tooltipContent, props, {
                    estimatedWidth: tooltipEstimatedWidth,
                  })
              : tooltipContent
          }
        />
        {series.map((entry) => (
          <Bar
            key={entry.key}
            dataKey={entry.key}
            stackId={entry.stackId}
            fill={`var(--color-${entry.key})`}
            radius={entry.radius}
            shape={(props: BarShapeProps) => (
              <DimmableBar {...props} xKey={xKey} />
            )}
          />
        ))}
        {overlay}
      </BarChart>
    </ChartContainer>
  );
};
